import Vue from 'vue'
import { durationOfEventSeconds } from '@utils/data/date-time'
import * as d3 from 'd3'
import {
  toArray,
  omitBy,
  defaults,
  keys,
  isNull,
  toPairs,
  fromPairs,
  sortBy,
} from 'lodash'

const ConsoleLogVars = false

export const getters = {
  cachedDeviceEventsSortedCycleAsArrayObjects(state, getters, rootState, rootGetters) {
    let cachedDeviceEvents = [...(rootGetters['deviceInsights/cachedDeviceEvents'] || [])]

    let filteredDeviceEvents = cachedDeviceEvents.filter((de) => {
      return de.cycleCountSequence === 1
    })

    let cachedDeviceEventsSortedCycle = d3
      .nest()

      .key((d) => {
        return d.cycle.name
      })
      .key((d) => {
        return Vue.moment(d.cycle.start).format()
      })
      .object(filteredDeviceEvents)

    let transformDeviceEvents = function(deviceEventToTransform, i, deviceEvents) {
      let deviceEvent = { ...deviceEventToTransform }

      if ((i > 0) & (i < deviceEvents.length)) {
        deviceEvent.end = deviceEvents[i - 1].start
        deviceEvent.duration = durationOfEventSeconds(
          Vue.moment,
          deviceEvent.start,
          deviceEvents[i - 1].start
        )
      }
      return deviceEvent
    }

    let cachedCycleEvent = Object.entries(cachedDeviceEventsSortedCycle).map(
      ([prop, value]) => ({
        name: prop,

        cycleEvents: Object.entries(value).map(([prop, value]) => ({
          cycleEvent: prop,
          deviceEvents: value.map(transformDeviceEvents),
        })),
      })
    )

    return cachedCycleEvent
  },

  cachedDeviceInsightsWithFiltersSortedStatusFormattedAsArrayObjects(
    state,
    getters,
    rootState,
    rootGetters
  ) {
    let cachedDeviceInsightsWithFilters = [
      ...(rootGetters['deviceInsights/cachedDeviceInsightsWithFilters'] || []),
    ]

    let distinctSortedEntriesOfStatusNames = d3
      .nest()

      .key(function(d) {
        return d.statusName
      })

      .rollup((v) => d3.sum(v, (d) => d.minutesDuration), (d) => d.statusName)

      .entries(cachedDeviceInsightsWithFilters)

    if (ConsoleLogVars)
      console.log(
        'distinctSortedEntriesOfStatusNames',
        distinctSortedEntriesOfStatusNames
      )

    return distinctSortedEntriesOfStatusNames
  },

  cachedDeviceInsightsWithFiltersSortedActivityFormattedAsArrayObjects(
    state,
    getters,
    rootState,
    rootGetters
  ) {
    let cachedDeviceInsightsWithFilters = [
      ...(rootGetters['deviceInsights/cachedDeviceInsightsWithFilters'] || []),
    ]

    let distinctSortedEntriesOfActivityNames = d3
      .nest()

      .key(function(d) {
        return d.activityName
      })

      .rollup((v) => d3.sum(v, (d) => d.minutesDuration), (d) => d.statusName)

      .entries(cachedDeviceInsightsWithFilters)

    if (ConsoleLogVars)
      console.log(
        'distinctSortedEntriesOfActivityNames',
        distinctSortedEntriesOfActivityNames
      )

    return distinctSortedEntriesOfActivityNames
  },

  cachedDeviceInsightsWithFiltersSortedByHourFormattedAsArray(
    state,
    getters,
    rootState,
    rootGetters
  ) {
    let cachedDeviceInsightsWithFilters = [
      ...(rootGetters['deviceInsights/cachedDeviceInsightsWithFilters'] || []),
    ]

    if (ConsoleLogVars)
      console.log('cachedDeviceInsightsWithFilters', cachedDeviceInsightsWithFilters)

    let cachedDeviceInsightsWithFiltersNestByHourByStatus = d3
      .nest()

      .key((d) => {
        return d.hour
      })

      .key((d) => {
        return d.statusName
      })

      .rollup((v) => {
        return d3.sum(v, (d) => {
          return d.minutesDuration
        })
      })

      .object(cachedDeviceInsightsWithFilters)

    if (ConsoleLogVars)
      console.log(
        'cachedDeviceInsightsWithFiltersNestByHourByStatus',
        cachedDeviceInsightsWithFiltersNestByHourByStatus
      )

    let cachedDeviceInsightsWithFiltersNestByHourByStatusSortedAsKeyValue = Object.entries(
      cachedDeviceInsightsWithFiltersNestByHourByStatus
    ).map(([prop, value]) => ({
      x: prop,

      value: fromPairs(
        sortBy(
          toPairs(
            defaults(omitBy(value, isNull), getters.distinctSortedObjectOfStatusNames)
          ),
          0
        )
      ),
    }))

    if (ConsoleLogVars)
      console.log(
        'cachedDeviceInsightsWithFiltersNestByHourByStatusSortedAsKeyValue',
        cachedDeviceInsightsWithFiltersNestByHourByStatusSortedAsKeyValue
      )

    let cachedDeviceInsightsWithFiltersNestByHourByStatusSortedAsFlatArray = Object.entries(
      cachedDeviceInsightsWithFiltersNestByHourByStatusSortedAsKeyValue
    ).map(([prop, value]) => [prop, ...toArray(value.value)])

    if (ConsoleLogVars)
      console.log(
        'cachedDeviceInsightsWithFiltersNestByHourByStatusSortedAsFlatArray',
        cachedDeviceInsightsWithFiltersNestByHourByStatusSortedAsFlatArray
      )

    return cachedDeviceInsightsWithFiltersNestByHourByStatusSortedAsFlatArray
  },

  distinctSortedObjectOfStatusNames(state, getters, rootState, rootGetters) {
    let cachedDeviceInsights = [
      ...(rootGetters['deviceInsights/cachedDeviceInsights'] || []),
    ]

    let distinctSortedObjectOfStatusNames = keys(
      d3
        .nest()

        .key(function(d) {
          return d.statusName
        })

        .rollup((v) => d3.sum(v, (d) => d.minutesDuration), (d) => d.statusName)

        .object(cachedDeviceInsights)
    )

    if (ConsoleLogVars)
      console.log('distinctSortedObjectOfStatusNames', distinctSortedObjectOfStatusNames)

    return distinctSortedObjectOfStatusNames.reduce((a, b) => ((a[b] = 0), a), {})
  },

  distinctSortedArrayOfStatusNames(state, getters, rootState, rootGetters) {
    return sortBy(toPairs(getters.distinctSortedObjectOfStatusNames))
  },

  distinctArrayOfShiftPatternNames(state, getters, rootState, rootGetters) {
    let cachedDeviceInsightsWithFilters = [
      ...(rootGetters['deviceInsights/cachedDeviceInsightsWithFilters'] || []),
    ]
    return [
      ...new Set(
        cachedDeviceInsightsWithFilters.map(
          (shiftPattern) => shiftPattern.shiftPatternName
        )
      ),
    ]
  },
}

export const actions = {}
